'use client';

import { useState } from 'react';

import { SegmentedControl, Stack, Text } from '@mantine/core';

import { CONTENT_LOCALES, LOCALE_LABELS, type ContentLocale } from '../lib/content/constants';
import { serializeAppLocaleCookie } from '../lib/portal/locale';

/**
 * The site-wide language preference.
 *
 * Written straight to `document.cookie` rather than posted to an endpoint: the
 * value authorises nothing and is validated on every read, so a round trip would
 * buy nothing but latency. The portal is server-rendered, which is why this is a
 * cookie at all — `localStorage` is invisible to the server, and the first paint
 * would be in the wrong language.
 *
 * `router.refresh()` is deliberately not called. This page has no server-rendered
 * copy that depends on the choice, and the portal reads the cookie fresh on its
 * next request.
 */
export default function LanguageSelector({ initialLocale }: { initialLocale: ContentLocale }) {
  const [locale, setLocale] = useState<ContentLocale>(initialLocale);

  function choose(value: string) {
    const chosen = CONTENT_LOCALES.find((candidate) => candidate === value);
    if (!chosen) {
      return;
    }

    setLocale(chosen);
    document.cookie = serializeAppLocaleCookie(chosen);
  }

  return (
    <Stack gap={8}>
      <Text component="label" id="echoplay-language-label" size="sm" c="#b8b8b8">
        Language
      </Text>

      <SegmentedControl
        aria-labelledby="echoplay-language-label"
        value={locale}
        onChange={choose}
        data={CONTENT_LOCALES.map((candidate) => ({
          value: candidate,
          label: `${LOCALE_LABELS[candidate].displayName} (${LOCALE_LABELS[candidate].nativeName})`,
        }))}
        styles={{
          root: { background: 'rgba(255,255,255,0.06)', alignSelf: 'flex-start' },
          label: { color: '#c9ccd4' },
        }}
      />

      <Text size="xs" c="#8b8f9a">
        Applies across EchoPlay, including the Echo Engine and EchoPrint developer portals.
      </Text>
    </Stack>
  );
}
